import React, { useState, useEffect } from 'react';
import {
    Building2, PlusCircle, Trash2, LogOut, Search,
    Pencil, Loader2, Users, FileText, Mail, Calendar,
    PlayCircle, Filter, DollarSign, Ban, CheckCircle,
    BarChart3, Send, Image as ImageIcon, Wifi, WifiOff, AlertTriangle, Clock
} from 'lucide-react';
import api from '../services/api'; // Axios Local
import Modal from '../components/Modal';

const SuperAdminScreen = ({ onLogout }) => {
    const [companies, setCompanies] = useState([]);
    const [loading, setLoading] = useState(true);

    // Configurações do Sistema
    const [systemSettings, setSystemSettings] = useState({ allowTrialRegistration: false });
    const [settingsUpdating, setSettingsUpdating] = useState(false);

    // Filtros e Modais
    const [searchTerm, setSearchTerm] = useState('');
    const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
    const [isEditModalOpen, setIsEditModalOpen] = useState(false);
    const [isProcessing, setIsProcessing] = useState(false);

    // Formulários
    const [newCompany, setNewCompany] = useState({
        name: '', adminName: '', adminEmail: '', adminPassword: '', cpf_cnpj: '', maxUsers: 3,
        modules: { financeiro: false, notas_fiscais: false }, next_due_date: ''
    });
    const [editingCompany, setEditingCompany] = useState(null);
    const [selectedDashboardCompany, setSelectedDashboardCompany] = useState(null);
    
    // Notificações
    const [isNoticeModalOpen, setIsNoticeModalOpen] = useState(false);
    const [noticeForm, setNoticeForm] = useState({ title: '', message: '', imageUrl: '', targetCompanyId: '' });

    // 1. CARREGAR EMPRESAS E CONFIGURAÇÕES DA API
    const fetchData = async () => {
        setLoading(true);
        try {
            const [compRes, setRes] = await Promise.all([
                api.get('/admin/companies'),
                api.get('/system/settings')
            ]);
            setCompanies(compRes.data);
            setSystemSettings(setRes.data || { allowTrialRegistration: false });
        } catch (error) {
            console.error("Erro ao carregar dados iniciais:", error);
            alert("Erro de conexão com servidor local.");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchData();
    }, []);

    // 1.1 ATUALIZAR CONFIGURAÇÃO (TRIAL)
    const handleToggleTrial = async () => {
        setSettingsUpdating(true);
        const newValue = !systemSettings.allowTrialRegistration;
        setSystemSettings({ ...systemSettings, allowTrialRegistration: newValue }); // optimistic update
        try {
            await api.put('/admin/settings', { allowTrialRegistration: newValue });
        } catch (error) {
            alert("Erro ao atualizar o Cadastro. Revertendo.");
            setSystemSettings({ ...systemSettings, allowTrialRegistration: !newValue }); // revert on error
        } finally {
            setSettingsUpdating(false);
        }
    };

    // 2. CRIAR EMPRESA
    const handleCreateCompany = async () => {
        setIsProcessing(true);
        try {
            await api.post('/register', newCompany); // Reusa rota de registro ou cria /admin/companies
            alert("Empresa criada com sucesso!");
            setIsCreateModalOpen(false);
            setNewCompany({ name: '', adminName: '', adminEmail: '', adminPassword: '', cpf_cnpj: '', maxUsers: 3, modules: { financeiro: false, notas_fiscais: false }, next_due_date: '' });
            fetchData();
        } catch (error) {
            alert("Erro ao criar empresa: " + (error.response?.data?.error || error.message));
        } finally {
            setIsProcessing(false);
        }
    };

    // 3. EDITAR EMPRESA (Alterar plano, status)
    const handleUpdateCompany = async () => {
        if (!editingCompany) return;
        setIsProcessing(true);
        try {
            await api.put(`/admin/companies/${editingCompany.id}`, editingCompany);
            alert("Empresa atualizada!");
            setIsEditModalOpen(false);
            setEditingCompany(null);
            fetchData();
        } catch (error) {
            alert("Erro ao atualizar.");
        } finally {
            setIsProcessing(false);
        }
    };

    // 4. SUSPENDER / EXCLUIR
    const handleToggleStatus = async (company) => {
        const newStatus = company.status === 'active' ? 'suspended' : 'active';
        if (!window.confirm(`Deseja alterar status para ${newStatus}?`)) return;

        try {
            await api.put(`/admin/companies/${company.id}`, { status: newStatus });
            fetchData();
        } catch (e) { alert("Erro ao alterar status"); }
    };

    const handleDeleteCompany = async (id) => {
        if (!window.confirm("ATENÇÃO: Isso apagará TODOS os dados desta empresa. Continuar?")) return;
        try {
            await api.delete(`/admin/companies/${id}`);
            fetchData();
        } catch (e) { alert("Erro ao excluir."); }
    };

    // 5. ENVIAR NOTIFICAÇÃO
    const handleSendNotice = async () => {
        if (!noticeForm.title || !noticeForm.message) return alert("Título e Mensagem são obrigatórios.");
        setIsProcessing(true);
        try {
            await api.post('/admin/system-notices', noticeForm);
            alert("Notificação enviada com sucesso!");
            setIsNoticeModalOpen(false);
            setNoticeForm({ title: '', message: '', imageUrl: '', targetCompanyId: '' });
        } catch (error) {
            alert("Erro ao enviar notificação.");
        } finally {
            setIsProcessing(false);
        }
    };

    // Filtro local
    const filteredCompanies = companies.filter(c =>
        (c.name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
        (c.owner_email || '').toLowerCase().includes(searchTerm.toLowerCase())
    );

    return (
        <div className="min-h-screen bg-gray-100 flex flex-col font-sans">
            {/* Header */}
            <header className="bg-gray-900 text-white shadow-lg z-10">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
                    <div className="flex items-center">
                        <Building2 className="w-6 h-6 mr-2 text-blue-400" />
                        <h1 className="text-xl font-bold tracking-wider">SUPER ADMIN (Local)</h1>
                    </div>
                    <button onClick={onLogout} className="flex items-center text-gray-300 hover:text-white px-3 py-2 rounded-md hover:bg-gray-800 transition-colors">
                        <LogOut className="w-5 h-5 mr-2" /> Sair
                    </button>
                </div>
            </header>

            {/* Conteúdo */}
            <main className="flex-1 max-w-7xl w-full mx-auto px-4 py-8">

                {/* Dashboard Header c/ Configurações Globais */}
                <div className="bg-white p-4 rounded-xl shadow-sm border border-gray-200 mb-6 flex flex-col sm:flex-row justify-between items-center gap-4">
                    <div className="flex items-center gap-3">
                        <div className={`w-12 h-6 flex items-center bg-gray-300 rounded-full p-1 cursor-pointer transition-colors ${systemSettings.allowTrialRegistration ? 'bg-green-500' : 'bg-gray-300'} ${settingsUpdating ? 'opacity-50 pointer-events-none' : ''}`} onClick={handleToggleTrial}>
                            <div className={`bg-white w-4 h-4 rounded-full shadow-md transform transition-transform duration-300 ${systemSettings.allowTrialRegistration ? 'translate-x-6' : ''}`}></div>
                        </div>
                        <div>
                            <p className="font-bold text-gray-800 text-sm">Cadastro: Testar Grátis</p>
                            <p className="text-xs text-gray-500">{systemSettings.allowTrialRegistration ? 'Aberta para novos visitantes (Landing Page)' : 'Fechada (Aparece como "Desativado temporariamente")'}</p>
                        </div>
                    </div>
                </div>

                <div className="flex flex-col md:flex-row justify-between items-center mb-6 gap-4">
                    <div className="relative w-full md:w-96">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-5 h-5" />
                        <input
                            type="text"
                            placeholder="Buscar empresa ou email..."
                            value={searchTerm}
                            onChange={e => setSearchTerm(e.target.value)}
                            className="w-full pl-10 pr-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 outline-none"
                        />
                    </div>
                    <div className="flex gap-2">
                        <button
                            onClick={() => { setNoticeForm({ ...noticeForm, targetCompanyId: '' }); setIsNoticeModalOpen(true); }}
                            className="bg-purple-600 text-white px-4 py-2 rounded-lg hover:bg-purple-700 flex items-center font-medium shadow-sm"
                        >
                            <Send className="w-5 h-5 mr-2" /> Notificar Clientes
                        </button>
                        <button
                            onClick={() => setIsCreateModalOpen(true)}
                            className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 flex items-center font-medium shadow-sm"
                        >
                            <PlusCircle className="w-5 h-5 mr-2" /> Nova Empresa
                        </button>
                    </div>
                </div>

                {loading ? (
                    <div className="text-center py-20"><Loader2 className="w-10 h-10 animate-spin mx-auto text-blue-600" /></div>
                ) : (
                    <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                        <table className="w-full text-left border-collapse">
                            <thead className="bg-gray-50 text-xs uppercase text-gray-500 font-bold border-b">
                                <tr>
                                    <th className="px-6 py-4">Empresa</th>
                                    <th className="px-6 py-4">Status</th>
                                    <th className="px-6 py-4">Vencimento</th>
                                    <th className="px-6 py-4">WhatsApp</th>
                                    <th className="px-6 py-4">Métricas Básicas</th>
                                    <th className="px-6 py-4 text-center">Ações</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-100 text-sm">
                                {filteredCompanies.map(company => {
                                    const isExpired = company.next_due_date && new Date(company.next_due_date) < new Date();
                                    const isConnected = company.connectionStatus === 'open'; // open = conectado no Baileys

                                    const getCreationDate = (c) => {
                                        if (c.createdAt) return new Date(c.createdAt);
                                        const id = c._id || c.id;
                                        if (id && typeof id === 'string' && id.length === 24) {
                                            return new Date(parseInt(id.substring(0, 8), 16) * 1000);
                                        }
                                        return new Date();
                                    };

                                    return (
                                    <tr key={company.id || company._id} className="hover:bg-gray-50 transition-colors">
                                        <td className="px-6 py-4">
                                            <p className="font-bold text-gray-900">{company.name}</p>
                                            <div className="flex items-center text-gray-500 mt-1">
                                                <Mail className="w-3 h-3 mr-1" /> {company.owner_email}
                                            </div>
                                            <div className="flex flex-col gap-0.5 text-gray-400 text-xs mt-1">
                                                <div className="flex items-center">
                                                    <Calendar className="w-3 h-3 mr-1" /> Desde {getCreationDate(company).toLocaleDateString('pt-BR')}
                                                </div>
                                                {company.lastLogin && (
                                                    <div className="flex items-center text-blue-500 font-medium">
                                                        <CheckCircle className="w-3 h-3 mr-1" /> Último login: {new Date(company.lastLogin).toLocaleDateString('pt-BR')} às {new Date(company.lastLogin).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}
                                                    </div>
                                                )}
                                            </div>
                                        </td>
                                        <td className="px-6 py-4">
                                            <span className={`px-2 py-1 rounded-full text-xs font-bold flex w-fit items-center gap-1
                                                ${company.status === 'active' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                                                {company.status === 'active' ? <CheckCircle className="w-3 h-3" /> : <Ban className="w-3 h-3" />}
                                                {company.status === 'active' ? 'Ativo' : 'Suspenso'}
                                            </span>
                                        </td>
                                        <td className="px-6 py-4">
                                            {company.next_due_date ? (
                                                <div className={`flex items-center text-sm font-medium ${isExpired ? 'text-red-600' : 'text-gray-700'}`}>
                                                    {isExpired && <AlertTriangle className="w-4 h-4 mr-1" />}
                                                    {new Date(company.next_due_date).toLocaleDateString('pt-BR')}
                                                </div>
                                            ) : (
                                                <span className="text-gray-400 text-xs italic">Não definido</span>
                                            )}
                                        </td>
                                        <td className="px-6 py-4">
                                            <div className={`flex items-center gap-1.5 text-xs font-bold px-2 py-1 rounded-md w-fit
                                                ${isConnected ? 'bg-green-50 text-green-700 border border-green-200' : 'bg-gray-100 text-gray-500 border border-gray-200'}`}>
                                                {isConnected ? <Wifi className="w-3 h-3" /> : <WifiOff className="w-3 h-3" />}
                                                {isConnected ? 'Conectado' : 'Desconectado'}
                                            </div>
                                        </td>
                                        <td className="px-6 py-4">
                                            <div className="flex flex-col gap-1 text-xs text-gray-600">
                                                <div className="flex items-center"><Users className="w-3 h-3 mr-1 text-blue-500" /> {company.usersLimit || company.maxUsers} Limite Users</div>
                                                <div className="flex items-center"><Users className="w-3 h-3 mr-1 text-indigo-500" /> {company.clientCount || 0} Clientes</div>
                                            </div>
                                        </td>
                                        <td className="px-6 py-4">
                                            <div className="flex justify-center space-x-2">
                                                <button onClick={() => setSelectedDashboardCompany(company)} className="p-2 text-indigo-600 hover:bg-indigo-50 rounded-full" title="Dashboard Completo">
                                                    <BarChart3 className="w-4 h-4" />
                                                </button>
                                                <button onClick={() => { setEditingCompany(company); setIsEditModalOpen(true); }} className="p-2 text-blue-600 hover:bg-blue-50 rounded-full" title="Editar">
                                                    <Pencil className="w-4 h-4" />
                                                </button>
                                                <button onClick={() => handleToggleStatus(company)} className={`p-2 rounded-full ${company.status === 'active' ? 'text-orange-500 hover:bg-orange-50' : 'text-green-500 hover:bg-green-50'}`} title="Bloquear/Desbloquear">
                                                    <PlayCircle className="w-4 h-4" />
                                                </button>
                                                <button onClick={() => { setNoticeForm({ ...noticeForm, targetCompanyId: company._id || company.id }); setIsNoticeModalOpen(true); }} className="p-2 text-purple-600 hover:bg-purple-50 rounded-full" title="Enviar Notificação Direta">
                                                    <Send className="w-4 h-4" />
                                                </button>
                                                <button onClick={() => handleDeleteCompany(company._id || company.id)} className="p-2 text-red-600 hover:bg-red-50 rounded-full" title="Excluir">
                                                    <Trash2 className="w-4 h-4" />
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                        {filteredCompanies.length === 0 && <div className="p-10 text-center text-gray-400">Nenhuma empresa encontrada.</div>}
                    </div>
                )}
            </main>

            {/* MODAL CRIAR */}
            <Modal isOpen={isCreateModalOpen} onClose={() => setIsCreateModalOpen(false)} title="Nova Empresa" confirmText="Criar" onConfirm={handleCreateCompany} isLoading={isProcessing}>
                <div className="space-y-3">
                    <input type="text" placeholder="Nome da Empresa" className="w-full p-2 border rounded" value={newCompany.name} onChange={e => setNewCompany({ ...newCompany, name: e.target.value })} />
                    <input type="text" placeholder="Nome Admin" className="w-full p-2 border rounded" value={newCompany.adminName} onChange={e => setNewCompany({ ...newCompany, adminName: e.target.value })} />
                    <input type="email" placeholder="Email Admin" className="w-full p-2 border rounded" value={newCompany.adminEmail} onChange={e => setNewCompany({ ...newCompany, adminEmail: e.target.value })} />
                    <input type="password" placeholder="Senha Admin" className="w-full p-2 border rounded" value={newCompany.adminPassword} onChange={e => setNewCompany({ ...newCompany, adminPassword: e.target.value })} />
                    <input type="text" placeholder="CPF/CNPJ" className="w-full p-2 border rounded" value={newCompany.cpf_cnpj} onChange={e => setNewCompany({ ...newCompany, cpf_cnpj: e.target.value })} />
                    <div className="flex items-center gap-2">
                        <span>Limite Usuários:</span>
                        <input type="number" className="p-2 border rounded w-20" value={newCompany.maxUsers} onChange={e => setNewCompany({ ...newCompany, maxUsers: parseInt(e.target.value) })} />
                    </div>
                    <div className="flex flex-col gap-2 p-3 border rounded bg-gray-50">
                        <span className="font-semibold text-sm text-gray-700">Módulos:</span>
                        <label className="flex items-center gap-2 text-sm">
                            <input type="checkbox" checked={newCompany.modules.financeiro} onChange={e => setNewCompany({ ...newCompany, modules: { ...newCompany.modules, financeiro: e.target.checked } })} />
                            Gestão Financeira
                        </label>
                        <label className="flex items-center gap-2 text-sm">
                            <input type="checkbox" checked={newCompany.modules.notas_fiscais} onChange={e => setNewCompany({ ...newCompany, modules: { ...newCompany.modules, notas_fiscais: e.target.checked } })} />
                            Notas Fiscais
                        </label>
                    </div>
                </div>
            </Modal>

            {/* MODAL EDITAR */}
            {editingCompany && (
                <Modal isOpen={isEditModalOpen} onClose={() => setIsEditModalOpen(false)} title="Editar Empresa" confirmText="Salvar" onConfirm={handleUpdateCompany} isLoading={isProcessing}>
                    <div className="space-y-3">
                        <input type="text" placeholder="Nome da Empresa" className="w-full p-2 border rounded" value={editingCompany.name} onChange={e => setEditingCompany({ ...editingCompany, name: e.target.value })} />
                        <div className="flex items-center gap-2">
                            <span>Limite Usuários:</span>
                            <input type="number" className="p-2 border rounded w-20" value={editingCompany.maxUsers} onChange={e => setEditingCompany({ ...editingCompany, maxUsers: parseInt(e.target.value) })} />
                        </div>
                        <div className="flex items-center gap-2">
                            <span>Vencimento:</span>
                            <input type="date" className="p-2 border rounded w-full" value={editingCompany.next_due_date ? new Date(editingCompany.next_due_date).toISOString().split('T')[0] : ''} onChange={e => setEditingCompany({ ...editingCompany, next_due_date: e.target.value })} />
                        </div>
                        <select className="w-full p-2 border rounded" value={editingCompany.status} onChange={e => setEditingCompany({ ...editingCompany, status: e.target.value })}>
                            <option value="active">Ativo</option>
                            <option value="suspended">Suspenso</option>
                            <option value="overdue">Pagamento Pendente</option>
                        </select>
                        <div className="flex flex-col gap-2 p-3 border rounded bg-gray-50 mt-2">
                            <span className="font-semibold text-sm text-gray-700">Módulos:</span>
                            <label className="flex items-center gap-2 text-sm">
                                <input type="checkbox" checked={editingCompany.modules?.financeiro || false} onChange={e => setEditingCompany({ ...editingCompany, modules: { ...(editingCompany.modules || {}), financeiro: e.target.checked } })} />
                                Gestão Financeira
                            </label>
                            <label className="flex items-center gap-2 text-sm">
                                <input type="checkbox" checked={editingCompany.modules?.notas_fiscais || false} onChange={e => setEditingCompany({ ...editingCompany, modules: { ...(editingCompany.modules || {}), notas_fiscais: e.target.checked } })} />
                                Notas Fiscais
                            </label>
                        </div>
                    </div>
                </Modal>
            )}

            {/* MODAL DASHBOARD DA EMPRESA */}
            {selectedDashboardCompany && (
                <Modal isOpen={!!selectedDashboardCompany} onClose={() => setSelectedDashboardCompany(null)} title={`Dashboard: ${selectedDashboardCompany.name}`} confirmText="Fechar" onConfirm={() => setSelectedDashboardCompany(null)}>
                    <div className="space-y-4">
                        <div className="grid grid-cols-2 gap-4">
                            <div className="bg-blue-50 border border-blue-100 p-4 rounded-xl text-center">
                                <Users className="w-6 h-6 text-blue-600 mx-auto mb-2" />
                                <p className="text-2xl font-bold text-blue-900">{selectedDashboardCompany.clientCount || 0}</p>
                                <p className="text-xs text-blue-600 uppercase font-semibold mt-1">Clientes Salvos</p>
                            </div>
                            <div className="bg-indigo-50 border border-indigo-100 p-4 rounded-xl text-center">
                                <FileText className="w-6 h-6 text-indigo-600 mx-auto mb-2" />
                                <p className="text-2xl font-bold text-indigo-900">{selectedDashboardCompany.ticketCount || 0}</p>
                                <p className="text-xs text-indigo-600 uppercase font-semibold mt-1">Atendimentos (Total)</p>
                            </div>
                            <div className="bg-purple-50 border border-purple-100 p-4 rounded-xl text-center">
                                <Building2 className="w-6 h-6 text-purple-600 mx-auto mb-2" />
                                <p className="text-2xl font-bold text-purple-900">{selectedDashboardCompany.userCount || 0}</p>
                                <p className="text-xs text-purple-600 uppercase font-semibold mt-1">Usuários/Atendentes</p>
                            </div>
                            <div className={`border p-4 rounded-xl text-center ${selectedDashboardCompany.connectionStatus === 'open' ? 'bg-green-50 border-green-100' : 'bg-gray-50 border-gray-200'}`}>
                                {selectedDashboardCompany.connectionStatus === 'open' ? <Wifi className="w-6 h-6 text-green-600 mx-auto mb-2" /> : <WifiOff className="w-6 h-6 text-gray-400 mx-auto mb-2" />}
                                <p className={`text-lg font-bold ${selectedDashboardCompany.connectionStatus === 'open' ? 'text-green-900' : 'text-gray-700'}`}>
                                    {selectedDashboardCompany.connectionStatus === 'open' ? 'Conectado' : 'Desconectado'}
                                </p>
                                <p className={`text-xs uppercase font-semibold mt-1 ${selectedDashboardCompany.connectionStatus === 'open' ? 'text-green-600' : 'text-gray-500'}`}>Status do WhatsApp</p>
                            </div>
                        </div>
                        
                        <div className="bg-gray-50 border border-gray-200 p-4 rounded-xl">
                            <h4 className="font-bold text-gray-800 mb-2 flex items-center"><Calendar className="w-4 h-4 mr-2" /> Detalhes da Assinatura</h4>
                            <div className="grid grid-cols-2 gap-y-2 text-sm">
                                <div className="text-gray-500">Plano Atual:</div>
                                <div className="font-semibold uppercase">{selectedDashboardCompany.plan || 'PRO'}</div>
                                <div className="text-gray-500">Vencimento:</div>
                                <div className="font-semibold">
                                    {selectedDashboardCompany.next_due_date ? new Date(selectedDashboardCompany.next_due_date).toLocaleDateString('pt-BR') : 'Não definido'}
                                </div>
                                <div className="text-gray-500">Módulos:</div>
                                <div className="font-semibold">
                                    {selectedDashboardCompany.modules?.financeiro && <span className="bg-green-100 text-green-700 px-1.5 py-0.5 rounded text-xs mr-1">Financeiro</span>}
                                    {selectedDashboardCompany.modules?.notas_fiscais && <span className="bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded text-xs">NFS-e</span>}
                                    {!selectedDashboardCompany.modules?.financeiro && !selectedDashboardCompany.modules?.notas_fiscais && <span className="text-gray-400">Padrão</span>}
                                </div>
                            </div>
                        </div>
                    </div>
                </Modal>
            )}

            {/* MODAL NOTIFICAÇÃO DO SISTEMA */}
            <Modal isOpen={isNoticeModalOpen} onClose={() => setIsNoticeModalOpen(false)} title="Aviso do Sistema" confirmText="Enviar Notificação" onConfirm={handleSendNotice} isLoading={isProcessing}>
                <div className="space-y-4">
                    <div className="bg-blue-50 border border-blue-200 p-3 rounded-lg flex items-start gap-3">
                        <Send className="w-5 h-5 text-blue-600 shrink-0 mt-0.5" />
                        <div className="text-sm text-blue-800">
                            <p className="font-semibold mb-1">Como funciona?</p>
                            <p>O aviso aparecerá para os usuários quando eles logarem ou acessarem a plataforma. Você pode direcionar para uma empresa específica ou para <strong>todos os clientes</strong> do sistema.</p>
                        </div>
                    </div>
                    
                    <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-1">Empresa Alvo</label>
                        <select className="w-full p-2 border rounded-lg" value={noticeForm.targetCompanyId} onChange={e => setNoticeForm({ ...noticeForm, targetCompanyId: e.target.value })}>
                            <option value="">Todas as Empresas (Aviso Global)</option>
                            {companies.map(c => (
                                <option key={c._id || c.id} value={c._id || c.id}>{c.name}</option>
                            ))}
                        </select>
                    </div>

                    <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-1">Título</label>
                        <input type="text" placeholder="Ex: Manutenção Programada, Nova Funcionalidade..." className="w-full p-2 border rounded-lg" value={noticeForm.title} onChange={e => setNoticeForm({ ...noticeForm, title: e.target.value })} />
                    </div>

                    <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-1">Mensagem</label>
                        <textarea rows={4} placeholder="Escreva o aviso detalhado aqui..." className="w-full p-2 border rounded-lg resize-none" value={noticeForm.message} onChange={e => setNoticeForm({ ...noticeForm, message: e.target.value })} />
                    </div>

                    <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-1">URL da Imagem (Opcional)</label>
                        <div className="flex gap-2">
                            <div className="relative flex-1">
                                <ImageIcon className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" />
                                <input type="text" placeholder="https://..." className="w-full pl-9 p-2 border rounded-lg" value={noticeForm.imageUrl} onChange={e => setNoticeForm({ ...noticeForm, imageUrl: e.target.value })} />
                            </div>
                        </div>
                        <p className="text-xs text-gray-500 mt-1">Cole o link de uma imagem hospedada para ilustrar o aviso.</p>
                    </div>
                </div>
            </Modal>
        </div>
    );
};

export default SuperAdminScreen;